import React, { useEffect, useState, useMemo } from "react";
import { formatDistanceToNow, isEqual, isBefore } from "../utils/dateUtils";
import { Link } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import "./ProjectList.css";

const db = getFirestore();

function compareDates(lDate) {
  let current = new Date();
  if (!lDate) return true;
  return lDate.toDate() <= current;
}

function isOlder(d) {
  let current = new Date();
  d.setHours(0, 0, 0, 0);
  current.setHours(0, 0, 0, 0);
  return isBefore(d, current);
}

function isToday(d) {
  let current = new Date();
  d.setHours(0, 0, 0, 0);
  current.setHours(0, 0, 0, 0);
  if (isEqual(d, current)) {
    return true;
  }
  return false;
}

const ProjectList = ({ projects, sortBy = "dueDate-asc" }) => {
  const [parentProjects, setParentProjects] = useState({});

  // Fetch parent project info for stacked assignments
  useEffect(() => {
    const fetchParentProjects = async () => {
      const parents = {};
      
      const stackedProjects = projects.filter(p => p.isStacked && p.parentProjectId);
      
      for (const stacked of stackedProjects) {
        if (!parents[stacked.parentProjectId]) {
          const parentRef = doc(db, "projects", stacked.parentProjectId);
          const parentSnap = await getDoc(parentRef);
          
          if (parentSnap.exists()) {
            parents[stacked.parentProjectId] = {
              id: parentSnap.id,
              ...parentSnap.data()
            };
          }
        }
      }
      
      setParentProjects(parents);
    };

    if (projects.length > 0) {
      fetchParentProjects();
    }
  }, [projects]);

  // Count incomplete stacked assignments for each parent
  const getIncompleteStackedCount = (projectId) => {
    return projects.filter(
      p => p.parentProjectId === projectId && !p.completed
    ).length;
  };

  // Count completed stacked assignments for each parent
  const getCompletedStackedCount = (projectId) => {
    return projects.filter(
      p => p.parentProjectId === projectId && p.completed
    ).length;
  };

  // Separate completed parents and incomplete assignments
  const completedParentsWithPendingStacked = projects.filter(
    project => 
      !project.isStacked && 
      project.completed && 
      getIncompleteStackedCount(project.id) > 0
  );

  const incompleteAssignments = projects.filter(
    project => !project.completed && compareDates(project.liveDate)
  );

  // Combine both lists
  let displayProjects = [
    ...completedParentsWithPendingStacked,
    ...incompleteAssignments
  ];

  // Apply sorting based on sortBy prop
  const sortedProjects = [...displayProjects].sort((a, b) => {
    switch (sortBy) {
      case "dueDate-asc":
        return a.dueDate.toDate() - b.dueDate.toDate();
      case "dueDate-desc":
        return b.dueDate.toDate() - a.dueDate.toDate();
      case "createdAt-asc":
        return a.createdAt.toDate() - b.createdAt.toDate();
      case "createdAt-desc":
        return b.createdAt.toDate() - a.createdAt.toDate();
      case "category":
        return a.category.localeCompare(b.category);
      case "title":
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  if (sortedProjects.length === 0) {
    return (
      <div className="project-list">
        <p>No projects yet!</p>
      </div>
    );
  }

  return (
    <div className="project-list">
      {sortedProjects.map((project) => {
        const isCompletedParent = project.completed && !project.isStacked;
        const incompleteStackedCount = isCompletedParent 
          ? getIncompleteStackedCount(project.id) 
          : 0;
        
        // For incomplete parents, check if they have any stacked assignments
        const isIncompleteParent = !project.completed && !project.isStacked;
        const completedChildCount = isIncompleteParent 
          ? getCompletedStackedCount(project.id) 
          : 0;
        const incompleteChildCount = isIncompleteParent 
          ? getIncompleteStackedCount(project.id) 
          : 0;
        const hasStackedChildren = completedChildCount > 0 || incompleteChildCount > 0;
        
        const parentProject = project.isStacked && project.parentProjectId
          ? parentProjects[project.parentProjectId]
          : null;

        return (
          <Link 
            to={`/projects/${project.id}`} 
            key={project.id}
            className={isCompletedParent ? "completed-parent-card" : ""}
          >
            <div className="project-header">
              <h4 style={{ textTransform: "capitalize" }}>
                {project.title}
                {project.isStacked && parentProject && (
                  <span className="stacked-badge">Stacked on: {parentProject.title}</span>
                )}
              </h4>
              {isCompletedParent && incompleteStackedCount > 0 && (
                <span className="completed-indicator">
                  ✓ Completed (📚 {incompleteStackedCount} pending)
                </span>
              )}
              {isIncompleteParent && hasStackedChildren && (
                <span className="stacked-status">
                  📚 {completedChildCount} done, {incompleteChildCount} pending
                </span>
              )}
            </div>
            
            <p style={{ textTransform: "capitalize" }}>{project.category}</p>
            
            <div id="project-dates">
              <div className="date-row">
                <p
                  className={`date ${
                    !isCompletedParent && isOlder(project.dueDate.toDate()) && "late"
                  } ${!isCompletedParent && isToday(project.dueDate.toDate()) && "today"}`}
                >
                  Due by <span>{project.dueDate.toDate().toDateString()}</span>
                </p>
              </div>
              <div className="date-row">
                <p>
                  Created:{" "}
                  {formatDistanceToNow(project.createdAt.toDate(), {
                    addSuffix: true,
                  })}
                </p>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default ProjectList;
